// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';

myw.CreateFeaturePlugin = myw.Plugin.extend({
    /**
     * @class Plugin to allow users to create features <br/>
     * Adds a button to the toolbar which when clicked will display a list with types of features the user can create.
     * When the user chooses one of the types, a form for the specified type will be activated
     * @param  {Application} owner  The application
     * @constructs
     * @extends {Plugin}
     */
    initialize(owner) {
        myw.Plugin.prototype.initialize.call(this, owner);

        this.map = this.app.map;
        this.system = this.app.system;
        this.editableFeaturesListHtml = null;
        this.featureDefs = null;
    },

    /**
     * show the create feature dialog
     */
    showDialog() {
        this.getContents().then(contents => {
            if (this.app.isHandheld) {
                this.app.layout.showCreateObjectPage(contents);
            } else {
                if (!this.addObjectDialog)
                    this.addObjectDialog = new CreateFeatureDialog({
                        owner: this,
                        contents: contents
                    });
                else {
                    this.addObjectDialog.setContent(contents).then(() => {
                        this.addObjectDialog.open();
                    });
                }
            }
        });
    },

    /**
     * get the editable features for the application
     * @returns {object<featureDD>} keyed on feature type
     * @private
     */
    async _getEditableFeatures() {
        if (!this._editableFeaturesPromise) {
            this._editableFeaturesPromise = this.system
                .userHasPermission('editFeatures')
                .then(hasPerm => {
                    this.hasPerm = hasPerm;
                    if (hasPerm) {
                        return _.keys(this.app.database.getEditableFeatureTypes());
                    } else {
                        return [];
                    }
                });
        }
        //DD info is not cached here as it can change
        //ex: in native app, master is temporarily inaccessible
        //also datasource can cache it
        const types = await this._editableFeaturesPromise;
        const featureDDs = await this.app.database.getDDInfoFor(types); //Returns all featureDDs (but need DDs, not types)
        const layerEditableFeatureTypes = {};
        //So create object of features editable on layer
        types.forEach(type => {
            const temp = this.app.database._decomposeFeatureType(type);
            layerEditableFeatureTypes[temp.typeInDs] = featureDDs[temp.typeInDs];
        });
        this.featureDefs = featureDDs;

        //Pick only from features on an editable layer
        return _.pick(
            layerEditableFeatureTypes,
            featureDD => featureDD.editable && featureDD.insert_from_gui
        );
    },

    /**
     * Get the contents to show in the create feature dialog or page
     * @return {promise<string|jqueryElement>} contents  Html object for the list of editable features
     *                                           If no editable features found, then return a message
     */
    getContents() {
        return this._getEditableFeatures()
            .then(featuresDD => {
                // Find out if the database is registered
                // and return only those features that come from editable datasources
                this.isDsRegistered = true;
                if (_.keys(featuresDD).length > 0) {
                    const featuresWithEditableDs = _.pick(featuresDD, featureDD =>
                        featureDD.datasource.isEditable()
                    );
                    this.isDsRegistered = _.keys(featuresWithEditableDs).length > 0;
                    return featuresWithEditableDs;
                } else {
                    return featuresDD;
                }
            })
            .then(featuresDD => {
                let contents;
                const messageEl = $('<div>', { class: 'createFeature-msg' });

                if (_.keys(featuresDD).length === 0 && this.isDsRegistered) {
                    contents = messageEl.text(this.msg('no_features'));
                } else if (!this.hasPerm) {
                    contents = messageEl.text(this.msg('not_authorised'));
                } else if (!this.isDsRegistered) {
                    contents = messageEl.text(this.msg('db_not_writable'));
                } else {
                    contents = $('<ul>', { class: 'createFeature-menu' });
                    const insertableFeaturesTypes = _.sortBy(featuresDD, 'external_name');
                    let listItem;

                    // build the html
                    _.each(insertableFeaturesTypes, featureDD => {
                        if (this.app.isFeatureEditable(featureDD.name)) {
                            listItem = $(
                                `<li class="newFeature enabled" id=${featureDD.ufn}>${featureDD.external_name}</li>`
                            );
                            contents.append(listItem);
                        }
                    });
                }
                return contents;
            });
    },

    /**
     * Displays a confirmation dialog if a user tries to replace an unsaved feature to add a new object
     * @private
     */
    addNewFeature(featureName) {
        const that = this;
        // If the current feature is unsaved, get user confirmation before creating the new feature
        if (this.app.currentFeature && this.app.currentFeature.isNew) {
            //ENH: use myw.ConfirmationDialog
            if (!this.confirmAddObject)
                this.confirmAddObject = this._createConfirmationDialog(featureName);

            this.addObjectDialog.close();

            this.confirmAddObject
                .dialog('option', {
                    title: this.msg('confirm_add', {
                        external_name: this.featureDefs[featureName].external_name
                    }),
                    buttons: [
                        {
                            text: this.msg('ok_btn'),
                            click() {
                                $(this).dialog('close');
                                that._createFeature(featureName);
                            }
                        },
                        {
                            text: this.msg('cancel_btn'),
                            class: 'right',
                            click() {
                                $(this).dialog('close');
                            }
                        }
                    ]
                })
                .dialog('widget')
                .find('.ui-dialog-buttonset')
                .css('float', 'none');

            this.confirmAddObject.dialog('open');
        } else {
            this._createFeature(featureName);
        }
    },

    /**
     * Add confirmation dialog html to the body
     * @private
     */
    _createConfirmationDialog(featureName) {
        //ENH: use myw.ConfirmationDialog
        const confirmAddObject = $(
            `<div id = 'addObjectConfirmation' class = 'hidden'>${this.msg('confirm_add_content', {
                featureType: this.app.currentFeature.featureDD.external_name
            })}</div>`
        )
            .appendTo('body')
            .dialog({
                modal: true,
                resizable: false,
                autoOpen: false
            });

        return confirmAddObject;
    },

    /**
     * start drawing a new feature
     * @param  {string} featureType
     * @private
     */
    _createFeature(featureType) {
        let referenceField;
        let keyOrReference;
        let referencedFeatureTitle;
        let referencedFeatureUrn;
        let referencedFeatureKeyName;
        const featureDD = this.featureDefs[featureType];
        const fieldsDD = featureDD.fields;
        const currentFeature = this.app.currentFeature;
        //Set the refernce field details if a current feature exists and is not detached/unsaved
        if (currentFeature && !currentFeature.isNew) {
            referencedFeatureTitle = currentFeature.getTitle();
            referencedFeatureUrn = currentFeature.getUrn();
            referencedFeatureKeyName = currentFeature.keyFieldName;
        }

        referenceField = this._checkForReferenceField(fieldsDD); //ENH This only returns one field name, no support for > 1 reference fields

        this.app.database.createDetachedFeature(featureType).then(detachedFeature => {
            if (
                referencedFeatureTitle &&
                referenceField &&
                !detachedFeature.properties[referenceField]
            ) {
                if (!detachedFeature.displayValues) detachedFeature.displayValues = {};
                // if type of field is reference then featureURN is stored in the database
                // if type of field is a foreign key then featureKey value is stored
                keyOrReference = fieldsDD[referenceField].type;
                if (keyOrReference === 'reference')
                    detachedFeature.properties[referenceField] = referencedFeatureUrn;
                else
                    detachedFeature.properties[referenceField] =
                        currentFeature.properties[referencedFeatureKeyName];

                detachedFeature.displayValues[referenceField] = referencedFeatureTitle;
            }

            this.app.setCurrentFeature(detachedFeature);
        });

        if (this.addObjectDialog) this.addObjectDialog.close();
    },

    /**
     * check if a field with type reference exists for feature
     * @param  {Object} fieldsDD Field dd object from the feature dd
     * @return {boolean|string} false if no reference field exists or more than one exist, internal name if one exists
     * @private
     */
    _checkForReferenceField(fieldsDD) {
        //ENH: add support for multiple reference fields
        const currentFeatureType = this.app.currentFeature && this.app.currentFeature.getType();
        const referenceFields = [];
        const hasAppropriateForeignKey = fieldDDType => {
            if (currentFeatureType && fieldDDType.indexOf('foreign_key') >= 0) {
                const splittedArray = fieldDDType.split('(', 2); // get feature type from foreign key field
                const featureType = splittedArray[1].slice(0, -1); //  remove last character from the string which is ")"
                return featureType == currentFeatureType;
            } else {
                return false;
            }
        };
        _.each(fieldsDD, fieldDD => {
            if (fieldDD.type == 'reference' || hasAppropriateForeignKey(fieldDD.type)) {
                referenceFields.push(fieldDD.internal_name);
            }
        });

        if (referenceFields.length == 1) {
            return referenceFields[0];
        } else {
            return false;
        }
    }
});

const CreateFeatureDialog = myw.Dialog.extend({
    messageGroup: 'CreateFeaturePlugin',
    className: 'createFeature-dialog',
    events: {
        'click .newFeature.enabled': '_addNewFeature'
    },
    options: {
        width: 360,
        position: { my: 'center', at: 'top+196', of: window },
        title: '{:create_title}'
    },

    /**
     * Asks the owner plugin to add the selected feature
     * @private
     */
    _addNewFeature(ev) {
        const selectedFeatureName = $(ev.currentTarget).prop('id');
        this.options.owner.addNewFeature(selectedFeatureName);
        this.options.owner.app.recordFunctionalityAccess(
            `core.toolbar.create_feature.${selectedFeatureName}`
        );
    }
});

myw.CreateFeaturePlugin.prototype.buttons = {
    dialog: myw.PluginButton.extend({
        id: 'a-createFeature', //for automated tests
        titleMsg: 'toolbar_msg',
        imgSrc: 'images/toolbar/write.svg',
        action() {
            this.owner.showDialog();
        }
    })
};

export default myw.CreateFeaturePlugin;
